import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import { Plus } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { Toaster } from '@/components/ui/toaster';
import type { ApiError } from '@/types';
import { AppLayout } from '@/components/layout/AppLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { HistoryTable, FilterPanel, Pagination, ExportMenu, type FilterValues } from '@/components/history';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { migrationsApi, migrationKeys, type MigrationsFilters } from '@/lib/api';
import { useAuth } from '@/hooks';
import { BatchHistoryTab } from '@/features/mass-migration/reporting/BatchHistoryTab';

interface SearchParams {
  page?: number | undefined;
  pageSize?: number | undefined;
  status?: string | undefined;
  search?: string | undefined;
  sortBy?: string | undefined;
  sortDesc?: boolean | undefined;
  startDate?: string | undefined;
  endDate?: string | undefined;
}

// The filter panel emits calendar dates ("YYYY-MM-DD"). Convert to an ISO instant in the
// browser's local timezone for the API's StartedAt range. The lower bound is start-of-day;
// the upper bound is end-of-day so the selected end date is included.
function toIsoStartOfDay(date: string | undefined): string | undefined {
  if (!date) return undefined;
  const d = new Date(`${date}T00:00:00`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

function toIsoEndOfDay(date: string | undefined): string | undefined {
  if (!date) return undefined;
  const d = new Date(`${date}T23:59:59.999`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

export const Route = createFileRoute('/migrations/')(({
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    pageSize: Number(search.pageSize) || 25,
    status: typeof search.status === 'string' ? search.status : undefined,
    search: typeof search.search === 'string' ? search.search : undefined,
    sortBy: typeof search.sortBy === 'string' ? search.sortBy : 'startedAt',
    // Default to most-recent-first (descending). Only an explicit `false` in the URL
    // (set when the operator toggles to ascending) flips it.
    sortDesc: search.sortDesc === undefined
      ? true
      : search.sortDesc === 'true' || search.sortDesc === true,
    startDate: typeof search.startDate === 'string' ? search.startDate : undefined,
    endDate: typeof search.endDate === 'string' ? search.endDate : undefined,
  }),
  component: MigrationsHistoryPage,
}));

function MigrationsHistoryPage() {
  const navigate = useNavigate();
  const searchParams = Route.useSearch();
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();

  // Local state for filters before URL update
  const [filters, setFilters] = useState<FilterValues>({
    search: searchParams.search ?? '',
    status: searchParams.status ? searchParams.status.split(',') as FilterValues['status'] : [],
    startDate: searchParams.startDate ?? '',
    endDate: searchParams.endDate ?? '',
  });

  // Build API filters from search params. Note the deliberate name mapping: the URL/UI
  // use `search`/`startDate`/`endDate`, while the API expects `searchTerm` and an ISO
  // `startDateFrom`/`startDateTo` range. The date inputs are calendar dates, so the upper
  // bound is widened to end-of-day to make the range inclusive of the chosen end date.
  const apiFilters: MigrationsFilters = {
    pageNumber: searchParams.page,
    pageSize: searchParams.pageSize,
    status: searchParams.status,
    searchTerm: searchParams.search,
    startDateFrom: toIsoStartOfDay(searchParams.startDate),
    startDateTo: toIsoEndOfDay(searchParams.endDate),
    sortBy: searchParams.sortBy,
    sortDesc: searchParams.sortDesc,
  };

  // Fetch migrations list
  const {
    data: migrationsData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: migrationKeys.list(apiFilters),
    queryFn: () => migrationsApi.list(apiFilters),
    enabled: isAuthenticated,
    placeholderData: (previousData) => previousData,
  });

  // Retry mutation
  const { mutate: retryMigration } = useMutation({
    mutationFn: (id: string) => migrationsApi.retry(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: migrationKeys.all });
    },
  });

  // Force fail mutation
  const [forcingId, setForcingId] = useState<string | null>(null);

  const { mutate: forceFailMigration } = useMutation({
    mutationFn: (id: string) => migrationsApi.forceFail(id),
    onMutate: (id) => setForcingId(id),
    onSettled: () => setForcingId(null),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: migrationKeys.lists() });
      toast({ title: 'Migration marked as failed' });
    },
    onError: (error: ApiError) => {
      toast({
        variant: 'destructive',
        title: 'Force fail failed',
        description: error.detail ?? error.title,
      });
    },
  });

  // Update URL with new search params
  const updateSearchParams = useCallback(
    (updates: Partial<SearchParams>) => {
      navigate({
        to: '/migrations',
        search: (prev: SearchParams) => ({
          ...prev,
          ...updates,
          // Reset to page 1 when filters change
          page: updates.page ?? 1,
        }),
        replace: true,
      });
    },
    [navigate]
  );

  // Handle filter changes
  const handleFiltersChange = useCallback(
    (newFilters: FilterValues) => {
      setFilters(newFilters);
      updateSearchParams({
        search: newFilters.search || undefined,
        status: newFilters.status.length > 0 ? newFilters.status.join(',') : undefined,
        startDate: newFilters.startDate || undefined,
        endDate: newFilters.endDate || undefined,
      });
    },
    [updateSearchParams]
  );

  // Handle sort change
  const handleSort = useCallback(
    (column: string) => {
      const isCurrentColumn = searchParams.sortBy === column;
      updateSearchParams({
        sortBy: column,
        sortDesc: isCurrentColumn ? !searchParams.sortDesc : true,
        page: searchParams.page,
      });
    },
    [searchParams.sortBy, searchParams.sortDesc, searchParams.page, updateSearchParams]
  );

  // Handle page change
  const handlePageChange = useCallback(
    (page: number) => {
      updateSearchParams({ page });
    },
    [updateSearchParams]
  );

  // Handle page size change
  const handlePageSizeChange = useCallback(
    (pageSize: number) => {
      updateSearchParams({ pageSize, page: 1 });
    },
    [updateSearchParams]
  );

  // Handle retry
  const handleRetry = useCallback(
    (id: string) => {
      retryMigration(id);
    },
    [retryMigration]
  );

  return (
    <AppLayout>
      <div className="container max-w-7xl py-8">
        <PageHeader
          title="Migration History"
          description="View and manage past migrations"
          actions={
            <div className="flex items-center gap-2">
              <ExportMenu migrations={migrationsData?.items ?? []} />
              <Button onClick={() => navigate({ to: '/migrations/new' })} className="gap-2">
                <Plus className="h-4 w-4" />
                New Migration
              </Button>
            </div>
          }
        />

        <Tabs defaultValue="single" className="space-y-4">
          <TabsList>
            <TabsTrigger value="single">Single Migrations</TabsTrigger>
            <TabsTrigger value="batches">Batches</TabsTrigger>
          </TabsList>

          <TabsContent value="single" className="space-y-4">
            {/* Filters */}
            <FilterPanel
              filters={filters}
              onChange={handleFiltersChange}
            />

            {/* Content */}
            {isLoading ? (
              <div className="flex items-center justify-center min-h-[400px]">
                <LoadingSpinner size="lg" label="Loading migrations..." />
              </div>
            ) : isError ? (
              <ErrorMessage
                title="Failed to Load Migrations"
                error={error}
                onRetry={() => refetch()}
              />
            ) : (
              <>
                {/* Table */}
                <HistoryTable
                  migrations={migrationsData?.items ?? []}
                  sortBy={searchParams.sortBy ?? 'startedAt'}
                  sortDesc={searchParams.sortDesc ?? true}
                  onSort={handleSort}
                  onRetry={handleRetry}
                  onForceFail={forceFailMigration}
                  isForcingId={forcingId ?? undefined}
                />

                {/* Pagination */}
                {migrationsData && migrationsData.totalCount > 0 && (
                  <Pagination
                    page={searchParams.page ?? 1}
                    pageSize={searchParams.pageSize ?? 25}
                    totalCount={migrationsData.totalCount}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    className="py-4"
                  />
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="batches">
            <BatchHistoryTab />
          </TabsContent>
        </Tabs>
      </div>
      <Toaster />
    </AppLayout>
  );
}
